import { createSignal, onMount } from "solid-js";
import { createStore } from "solid-js/store";

import createAsync from "@repo/signals/createAsync";
import createRetained from "@repo/signals/createRetained";
import assert from "@repo/utils/assert";
import UBoolean from "@repo/utils/UBoolean";
import GoogleMaps from "@repo/utils-client/external/GoogleMaps";

import { useError } from "../../error/ErrorProvider";
import Autocomplete from "../../inputs/Autocomplete";
import GoogleMapsSolid from "../GoogleMapsSolid";

export function createAutocompleteController({ defaultInput = "" } = {}) {
  const error = useError();

  const googleMaps = GoogleMapsSolid.useContext();

  const [input, setInput] = createSignal(defaultInput);
  let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;
  const [suggestionsByInput, setSuggestionsByInput] = createStore<
    Record<
      string,
      | Autocomplete.Suggestion[]
      // Pending
      | null
    >
  >({ "": [] });
  const [placeId, setPlaceId] = createSignal<GoogleMaps.Place.Id>();
  const place = createAsync(
    async () =>
      placeId() && GoogleMaps.Place.fetch(await googleMaps.load(), placeId()!),
    undefined,
    {
      onError(e) {
        error.capture(e);
      },
    }
  );

  onMount(async () => {
    if (!defaultInput) return;

    const client = await googleMaps.load();
    sessionToken = new client.places.AutocompleteSessionToken();

    const firstSuggestion = (
      await GoogleMaps.Place.Suggestion.fetch(client, defaultInput, {
        sessionToken,
        includedRegionCodes: googleMaps.regionCodes,
      })
    )[0];
    if (!firstSuggestion) return;

    const suggestion = Suggestion.forAutocomplete(firstSuggestion);
    if (!suggestion) return;

    setInput(suggestion.string!);
    setPlaceId(suggestion.value);
  });

  // Retain previous suggestions when suggestions for updated input are loading
  const suggestions = createRetained(() => suggestionsByInput[input()]);

  return {
    input,
    place,
    props: () =>
      ({
        value: input(),
        autocomplete: "off",
        suggestions: (!place() && suggestions()) || [],
        async onInput(e) {
          const input = e.currentTarget.value;

          setInput(input);
          setPlaceId();

          if (!input) return;

          const client = await googleMaps.load();

          if (!sessionToken)
            sessionToken = new client.places.AutocompleteSessionToken();

          if (suggestionsByInput[input] !== undefined) return;

          // Prevent refetch
          setSuggestionsByInput(input, null);
          setSuggestionsByInput(
            input,
            (
              await GoogleMaps.Place.Suggestion.fetch(client, input, {
                sessionToken,
                includedRegionCodes: googleMaps.regionCodes,
              })
            )
              .map(Suggestion.forAutocomplete)
              .filter(UBoolean.isTruthy)
          );
        },
        async onApplySuggestion(suggestion) {
          assert("string" in suggestion);

          setPlaceId(suggestion.value);
          setInput(suggestion.string!);
          sessionToken = null;
        },
      }) satisfies Partial<Autocomplete.Props>,
  };
}

export namespace Suggestion {
  export const forAutocomplete = (
    suggestion: google.maps.places.AutocompleteSuggestion
  ):
    | Extract<Autocomplete.Suggestion, { matchedSubstrings: any }>
    | undefined => {
    const { placePrediction } = suggestion;
    if (!placePrediction) return;

    return {
      value: placePrediction.placeId,
      string: placePrediction.text.text,
      matchedSubstrings: placePrediction.text.matches.map(
        ({ startOffset, endOffset }) => [startOffset, endOffset]
      ),
    };
  };
}
